import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchSharedView } from '../apiService';
import './SharedView.css';

/**
 * Public, read-only view of a shared trade show plan.
 *
 * Deliberately rendered outside the dashboard layout: the viewer has no Zibit
 * account, so there is no sidebar, no navigation and nothing editable.
 */
function SharedView() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const res = await fetchSharedView(token);
      if (cancelled) return;
      if (res.success) {
        setState({ loading: false, data: res.data, error: '' });
      } else {
        setState({ loading: false, data: null, error: res.error });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return <div className="sv-page"><div className="sv-message">Loading…</div></div>;
  }

  if (state.error) {
    return (
      <div className="sv-page">
        <div className="sv-message">
          <h2>This link isn't available</h2>
          <p>It may have been revoked, or the address may be incomplete.
             Check with whoever sent it to you.</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const has = (key) => Array.isArray(d.sections) && d.sections.includes(key);

  return (
    <div className="sv-page">
      <header className="sv-header">
        <div className="sv-brand">zibit</div>
        <h1>{d.eventName}</h1>
        <p className="sv-shared-by">
          Shared with you by {d.sharedBy}
          {d.sharedByEmail && <> · <a href={`mailto:${d.sharedByEmail}`}>{d.sharedByEmail}</a></>}
        </p>
      </header>

      <main className="sv-body">
        {has('showOverview') && (
          <Section title="Show Overview">
            <Fields data={d.showOverview} labels={{
              showName: 'Show', venue: 'Venue', market: 'Market',
              eventDate: 'Start date', endDate: 'End date',
              exhibitHall: 'Exhibit hall', targetAudience: 'Target audience',
              location: 'Location', locationAddress: 'Address', notes: 'Notes',
            }} />
          </Section>
        )}

        {has('generalInformation') && (
          <Section title="General Information">
            <Fields data={d.generalInformation} labels={{
              boothNumber: 'Booth number', boothSize: 'Booth size',
              departure: 'Departure', pickupNotes: 'Pickup notes',
              showAttireNotes: 'Attire', badgesAndBoothConduct: 'Booth conduct',
              badgePickup: 'Badge pickup',
            }} />
          </Section>
        )}

        {has('travel') && (
          <Section title="Travel Information">
            <Table rows={d.travel} columns={[
              ['representativeName', 'Representative'], ['arrivalDate', 'Arrives'],
              ['departureDate', 'Departs'], ['hotelName', 'Hotel'],
              ['hotelAddress', 'Address'], ['hotelReservationNumber', 'Reservation'],
            ]} />
          </Section>
        )}

        {has('teamSchedule') && (
          <Section title="Team Schedule">
            <Table rows={d.teamSchedule} columns={[
              ['memberName', 'Name'], ['contactTitle', 'Title'],
              ['startDate', 'From'], ['endDate', 'To'],
            ]} />
            {d.teamNotes && <p className="sv-note">{d.teamNotes}</p>}
          </Section>
        )}

        {has('marketingSchedule') && (
          <Section title="Marketing Schedule">
            <Table rows={d.marketingSchedule} columns={[
              ['mediumName', 'Medium'], ['scheduleDate', 'Date'],
              ['keyMessages', 'Key messages'], ['callToAction', 'Call to action'],
              ['representativeName', 'Owner'],
            ]} />
          </Section>
        )}

        {has('boothMaterials') && (
          <Section title="Booth Materials">
            <Table rows={d.boothMaterials} columns={[
              ['itemType', 'Item'], ['description', 'Description'],
              ['quantity', 'Qty'], ['status', 'Status'], ['storageLocation', 'Storage'],
            ]} />
          </Section>
        )}

        {has('strategy') && (
          <Section title="Strategy">
            <Table rows={d.strategy} columns={[
              ['category', 'Goal'], ['questionText', 'Question'],
              ['answer', 'Answer'], ['additionalInfo', 'Notes'],
            ]} />
          </Section>
        )}

        {has('budget') && (
          <Section title="Budget">
            <Table rows={d.budget} columns={[
              ['category', 'Category'], ['itemName', 'Item'],
              ['description', 'Description'], ['budgetCost', 'Budget'],
              ['actualCost', 'Actual'], ['dueDate', 'Due'],
            ]} />
          </Section>
        )}

        {has('tasks') && (
          <Section title="Tasks">
            <Table rows={d.tasks} columns={[
              ['taskName', 'Task'], ['assignedTo', 'Assigned to'],
              ['dueDate', 'Due'], ['status', 'Status'],
            ]} />
          </Section>
        )}
      </main>

      <footer className="sv-footer">
        This is a read-only view shared from Zibit.
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="sv-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/** Renders a key/value block, skipping anything the owner left blank. */
function Fields({ data, labels }) {
  if (!data) return <p className="sv-empty">Nothing recorded yet.</p>;
  const entries = Object.entries(labels).filter(([key]) => {
    const v = data[key];
    return v !== null && v !== undefined && String(v).trim() !== '';
  });
  if (entries.length === 0) return <p className="sv-empty">Nothing recorded yet.</p>;
  return (
    <dl className="sv-fields">
      {entries.map(([key, label]) => (
        <div key={key} className="sv-field">
          <dt>{label}</dt>
          <dd>{String(data[key])}</dd>
        </div>
      ))}
    </dl>
  );
}

function Table({ rows, columns }) {
  if (!rows || rows.length === 0) return <p className="sv-empty">Nothing recorded yet.</p>;
  return (
    <div className="sv-table-wrap">
      <table className="sv-table">
        <thead>
          <tr>{columns.map(([key, label]) => <th key={key}>{label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map(([key]) => (
                <td key={key}>{row[key] === null || row[key] === undefined ? '' : String(row[key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SharedView;
